import React from 'react';
import { T } from '@design/index';
import NHCLogo from '@design/NHCLogo';

const WEB_LAYOUT_CSS = `
.nhc-web-entry{min-height:100dvh;display:flex;align-items:center;justify-content:center;background:#F1F5F9;padding:20px;box-sizing:border-box;font-family:${T.font}}
.nhc-web-inner{display:flex;width:100%;max-width:1060px;min-height:min(640px,90vh);border-radius:20px;overflow:hidden;box-shadow:0 8px 40px rgba(15,23,42,0.08)}
.nhc-web-brand{width:360px;flex-shrink:0;background:linear-gradient(170deg,${T.navyDk} 0%,${T.overlap} 55%,${T.teal} 100%);display:flex;flex-direction:column;justify-content:center;padding:48px 36px;color:#FFF;position:relative;overflow:hidden}
.nhc-web-form{flex:1;background:#FFF;display:flex;flex-direction:column;overflow-y:auto;min-width:0}
@media(max-width:767px){
  .nhc-web-entry{padding:0;background:#FFF;align-items:stretch}
  .nhc-web-inner{flex-direction:column;border-radius:0;box-shadow:none;min-height:100dvh}
  .nhc-web-brand{display:none}
  .nhc-web-form{border-radius:0}
}
`;

function BrandPanel() {
  return (
    <div className="nhc-web-brand">
      <svg width="390" height="60" viewBox="0 0 390 60" fill="none" style={{ position: 'absolute', bottom: 40, left: 0, opacity: 0.1 }}>
        <path d="M0,30 L80,30 L155,30 L165,48 L180,6 L195,48 L205,30 L390,30" stroke="#FFF" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      </svg>
      <NHCLogo w={52} variant="white" />
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 20, letterSpacing: '-0.02em' }}>NeoHealthCard</div>
      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 8, lineHeight: 1.7 }}>
        Your verified digital health identity — consent-based, provider-signed, and always under your control.
      </div>
      <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {[
          { icon: 'M12 1L2 5v8c0 7 4.3 11 10 13 5.7-2 10-6 10-13V5L12 1z', label: 'End-to-end encrypted records' },
          { icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', label: 'Verified provider network' },
          { icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Every access is logged & auditable' },
        ].map((p) => (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={p.icon} /></svg>
            </div>
            <span style={{ fontSize: 11, opacity: 0.9, lineHeight: 1.4 }}>{p.label}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 32, fontSize: 9, opacity: 0.5, lineHeight: 1.6 }}>
        Secure health identity · Consent-based records · Verified care network
      </div>
    </div>
  );
}

export function WebEntryLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <style>{WEB_LAYOUT_CSS}</style>
      <div className="nhc-web-entry">
        <div className="nhc-web-inner">
          <BrandPanel />
          <div className="nhc-web-form">
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

export const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 16,
  padding: 14,
  boxShadow: T.shadow,
  ...extra,
});

export const scrollBody: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

export const pill = (_text: string, color: string, filled = false): React.CSSProperties => ({
  fontSize: 9,
  fontWeight: 600,
  color: filled ? '#FFF' : color,
  background: filled ? color : `${color}12`,
  padding: '3px 10px',
  borderRadius: 6,
  fontFamily: T.font,
  border: filled ? 'none' : `1px solid ${color}25`,
});

export const actionBtn = (_label: string, bg: string, color: string, outline = false): React.CSSProperties => ({
  fontSize: 10,
  fontWeight: 600,
  color: outline ? bg : color,
  background: outline ? 'transparent' : bg,
  border: outline ? `1.5px solid ${bg}` : 'none',
  borderRadius: 8,
  padding: '7px 14px',
  cursor: 'pointer',
  fontFamily: T.font,
  whiteSpace: 'nowrap',
});

export function VerifiedBadge({ text, color }: { text: string; color: string }) {
  return <span style={{ fontSize: 7, fontWeight: 700, color: '#FFF', background: color, padding: '2px 8px', borderRadius: 4, fontFamily: T.font, letterSpacing: '0.03em' }}>{text}</span>;
}

export function SectionTitle({ text }: { text: string }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8 }}>{text}</div>;
}

export function ToggleSwitch({ on, disabled }: { on: boolean; disabled?: boolean }) {
  const active = on && !disabled;
  return (
    <div style={{ width: 38, height: 22, borderRadius: 11, background: active ? T.success : T.border, position: 'relative', flexShrink: 0, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'background 0.15s ease' }}>
      <div style={{ position: 'absolute', top: 2, left: active ? 18 : 2, width: 18, height: 18, borderRadius: 9, background: '#FFF', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transition: 'left 0.15s ease' }} />
    </div>
  );
}

export function ProgressHeader({ title, step, total, onBack }: { title: string; step: number; total: number; onBack?: () => void }) {
  return (
    <div style={{ padding: '12px 16px', background: T.white, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={T.textSec} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font }}>{title}</div>
          <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font }}>Step {step} of {total}</div>
        </div>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: T.border, marginTop: 6 }}>
        <div style={{ width: `${(step / total) * 100}%`, height: '100%', borderRadius: 2, background: T.teal }} />
      </div>
    </div>
  );
}
